import { initializeApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  getRedirectResult,
  indexedDBLocalPersistence,
  initializeAuth,
  inMemoryPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'
import { appConfig } from '@/config/config'

/** Ported from taptalent-account-management-frontend/src/lib/firebase.ts */

/**
 * Firebase throws `auth/invalid-api-key` from getAuth() at MODULE LOAD when apiKey is
 * empty, which takes down the whole app — including routes that never touch auth, like
 * /kitchen-sink. Until auth-workboard-staging exists there is no real key, so substitute a
 * placeholder and warn loudly rather than hard-crashing the app.
 *
 * The emulator accepts any non-empty key, so this also makes emulator-only dev work.
 */
const PLACEHOLDER_API_KEY = 'no-firebase-key-configured'

export const isFirebaseConfigured = Boolean(appConfig.firebase.apiKey)

if (!isFirebaseConfigured && import.meta.env.DEV) {
  console.warn(
    '[firebase] No API key configured. Sign-in will not work.\n' +
      'Set VITE_FIREBASE_API_KEY in .env.local, or fill in src/config/config.ts once the ' +
      'auth-workboard-* projects exist.',
  )
}

const app = initializeApp({
  ...appConfig.firebase,
  apiKey: appConfig.firebase.apiKey || PLACEHOLDER_API_KEY,
})

/**
 * localStorage FIRST, IndexedDB second. The order is the whole point — do not "fix" it.
 *
 * Firebase's IndexedDBLocalPersistence tears itself down when the page is backgrounded:
 *
 *   onVisibilityChange = () => { if (document.visibilityState === 'hidden') this.onPageHide() }
 *   onPageHide         = () => { this.isHiding = true; this.dbPromise.then(db => db.close()) }
 *   _openDb            = () => { if (this.isHiding) throw new Error('Database is closing/hidden') }
 *
 * signInWithPopup opens a window that takes focus, so the opener goes `hidden` and the
 * database closes. When the popup completes and the SDK writes the session back, _openDb
 * throws "Database is closing/hidden" and sign-in fails. It reproduces on every popup
 * sign-in where the opener loses focus.
 *
 * A fallback chain does NOT help here: Firebase picks the first *available* persistence,
 * and IndexedDB is available — it just breaks under focus loss. So the chain never falls
 * through, and IndexedDB has to not be first.
 *
 * browserLocalPersistence is plain localStorage with a `storage` event listener. It has no
 * visibility-dependent lifecycle, survives reloads and works across tabs. IndexedDB stays
 * in the chain only for environments where localStorage is unavailable.
 *
 * initializeAuth also requires popupRedirectResolver explicitly — omitting it makes
 * signInWithPopup/Redirect throw at call time.
 */
function createAuth() {
  try {
    return initializeAuth(app, {
      persistence: [
        browserLocalPersistence,
        indexedDBLocalPersistence,
        browserSessionPersistence,
        inMemoryPersistence,
      ],
      popupRedirectResolver: browserPopupRedirectResolver,
    })
  } catch (err) {
    // initializeAuth throws auth/already-initialized if it runs twice on the same app,
    // which Vite HMR does on every edit to this module. getAuth returns the existing
    // instance, persistence chain and all.
    if ((err as { code?: string }).code === 'auth/already-initialized') return getAuth(app)
    throw err
  }
}

export const auth = createAuth()

if (appConfig.useEmulator) {
  connectAuthEmulator(auth, appConfig.authEmulatorUrl, { disableWarnings: true })
}

export const googleProvider = new GoogleAuthProvider()

/*
 * No `hd` hint, deliberately.
 *
 * `hd` narrows Google's account picker to one domain. It was set to ours, which meant a
 * client arriving at the main sign-in was offered only @taptalent.ai accounts — so the one
 * account they actually have was the one they could not pick. They are entitled to sign in;
 * they are simply routed to their portal afterwards.
 *
 * Nothing is weakened by dropping it. `hd` was never enforcement — it is a picker
 * convenience — and access is decided by the backend: `/me` for members, `/portal/me` for
 * clients, nothing for anyone else.
 */

/**
 * Kept as a distinct export so the portal's sign-in path stays explicit at its call sites,
 * even though neither provider carries a domain hint any more.
 */
export const portalGoogleProvider = new GoogleAuthProvider()

export const signInWithGoogle = (provider: GoogleAuthProvider = googleProvider) =>
  signInWithPopup(auth, provider)

/** Popups are blocked in some embedded browsers and by Safari ITP; this is the fallback. */
export const signInWithGoogleRedirect = (provider: GoogleAuthProvider = googleProvider) =>
  signInWithRedirect(auth, provider)

/**
 * Completes a redirect sign-in. MUST be called on app start, or the redirect path silently
 * never finishes: Google bounces back, no session is established, and the user lands on the
 * login page again with nothing to explain why.
 *
 * Resolves to null when no redirect is pending, which is the normal popup case.
 */
export async function completeRedirectSignIn() {
  try {
    return await getRedirectResult(auth)
  } catch (err) {
    // Firebase stores pending-redirect state in IndexedDB. If that is unavailable — private
    // windows, aggressive privacy settings, or the page unloading mid-write — it surfaces as
    // "Database is closing"/"Database deleted by request of the user". Not recoverable here,
    // but it must not take the whole app down.
    console.error('[firebase] redirect sign-in could not be completed', err)
    return null
  }
}

export const firebaseSignOut = () => signOut(auth)
